import ClientOAuth2 from 'client-oauth2'

// Configuration OAuth2
interface OAuth2Config {
  clientId: string
  clientSecret?: string
  accessTokenUri: string
  authorizationUri: string
  redirectUri: string
  scopes?: string[]
}

class AuthService {
  private oauth2Client: ClientOAuth2
  private config: OAuth2Config
  private accessToken: string | null = null
  private tokenExpiry: Date | null = null

  constructor(config: OAuth2Config) {
    this.config = config
    this.oauth2Client = new ClientOAuth2({
      clientId: config.clientId,
      clientSecret: config.clientSecret,
      accessTokenUri: config.accessTokenUri,
      authorizationUri: config.authorizationUri,
      redirectUri: config.redirectUri,
      scopes: config.scopes || []
    })

    // Récupérer le token stocké au démarrage
    this.loadStoredToken()
  }

  /**
   * Authentification avec client credentials flow (machine-to-machine)
   * Utilise clientId et clientSecret pour obtenir un token directement
   */
  async authenticateClientCredentials(): Promise<string> {
    try {
      console.log('🔐 Demande d\'authentification client credentials...')

      const token = await this.oauth2Client.credentials.getToken()

      this.accessToken = token.accessToken
      // Calculer la date d'expiration si expiresIn est fourni
      const expiresInMs = token.expiresIn ? parseInt(String(token.expiresIn), 10) : null
      this.tokenExpiry = expiresInMs ? new Date(Date.now() + expiresInMs * 1000) : null

      // Stocker le token
      this.storeToken(token.accessToken, this.tokenExpiry || undefined)

      console.log('✅ Token d\'accès obtenu avec succès')
      return token.accessToken

    } catch (error) {
      console.error('❌ Erreur lors de l\'authentification:', error)
      throw new Error('Échec de l\'authentification OAuth2')
    }
  }

  /**
   * Obtenir l'URL d'autorisation pour le flow authorization code
   */
  getAuthorizationUrl(): string {
    return this.oauth2Client.code.getUri()
  }

  /**
   * Échanger le code d'autorisation contre un token d'accès
   */
  async exchangeCodeForToken(_authorizationCode: string): Promise<string> {
    try {
      console.log('🔄 Échange du code d\'autorisation contre un token...')

      const token = await this.oauth2Client.code.getToken(window.location.href)

      this.accessToken = token.accessToken
      // Calculer la date d'expiration si expiresIn est fourni
      const expiresInMs = token.expiresIn ? parseInt(String(token.expiresIn), 10) : null
      this.tokenExpiry = expiresInMs ? new Date(Date.now() + expiresInMs * 1000) : null

      // Stocker le token
      this.storeToken(token.accessToken, this.tokenExpiry || undefined)

      console.log('✅ Token d\'accès obtenu avec succès')
      return token.accessToken

    } catch (error) {
      console.error('❌ Erreur lors de l\'échange du code:', error)
      throw new Error('Échec de l\'échange du code d\'autorisation')
    }
  }

  /**
   * Vérifier si le token est valide
   */
  isTokenValid(): boolean {
    if (!this.accessToken) return false
    if (!this.tokenExpiry) return true // Si pas d'expiration connue, on considère valide

    // Ajouter une marge de 5 minutes pour éviter les expirations en cours de requête
    const now = new Date()
    const margin = 5 * 60 * 1000 // 5 minutes en millisecondes

    return this.tokenExpiry.getTime() > (now.getTime() + margin)
  }

  /**
   * Obtenir le token d'accès actuel
   */
  async getAccessToken(): Promise<string> {
    // Si le token n'est pas valide, tenter de l'obtenir
    if (!this.isTokenValid()) {
      await this.authenticateClientCredentials()
    }

    if (!this.accessToken) {
      throw new Error('Aucun token d\'accès disponible')
    }

    return this.accessToken
  }

  /**
   * Rafraîchir le token si nécessaire
   */
  async refreshTokenIfNeeded(): Promise<string> {
    if (this.isTokenValid()) {
      return this.accessToken!
    }

    return await this.authenticateClientCredentials()
  }

  /**
   * Déconnexion
   */
  logout(): void {
    this.accessToken = null
    this.tokenExpiry = null
    this.clearStoredToken()
    console.log('🚪 Déconnexion effectuée')
  }

  /**
   * Stocker le token dans le localStorage
   */
  private storeToken(token: string, expires?: Date): void {
    localStorage.setItem('cal_access_token', token)
    if (expires) {
      localStorage.setItem('cal_token_expiry', expires.toISOString())
    }
  }

  /**
   * Charger le token depuis le localStorage
   */
  private loadStoredToken(): void {
    const storedToken = localStorage.getItem('cal_access_token')
    const storedExpiry = localStorage.getItem('cal_token_expiry')

    if (storedToken) {
      this.accessToken = storedToken
      if (storedExpiry) {
        this.tokenExpiry = new Date(storedExpiry)
      }
    }
  }

  /**
   * Supprimer le token stocké
   */
  private clearStoredToken(): void {
    localStorage.removeItem('cal_access_token')
    localStorage.removeItem('cal_token_expiry')
  }
}

// Configuration par défaut - à adapter selon votre SSO
const defaultConfig: OAuth2Config = {
  clientId: import.meta.env.VITE_SSO_CLIENT_ID__FRONT_RDVLEGAL || 'your-client-id',
  clientSecret: import.meta.env.VITE_SSO_CLIENT_SECRET__FRONT_RDVLEGAL, // Optionnel pour client credentials
  accessTokenUri: import.meta.env.VITE_SSO_TOKEN_URL || 'https://your-sso.com/oauth/token',
  authorizationUri: import.meta.env.VITE_SSO_AUTHORIZE_URL || 'https://your-sso.com/oauth/authorize',
  redirectUri: import.meta.env.VITE_SSO_SILENT_REDIRECT_URI__FRONT_RDVLEGAL || `${window.location.origin}/auth/callback`,
  scopes: [import.meta.env.VITE_SSO_SCOPE__FRONT_RDVLEGAL]
}

// Instance singleton du service d'authentification
export const authService = new AuthService(defaultConfig)

export default AuthService
